/**
 * Lightweight i18n. Keyed by the English string itself so callers just do
 * t("Dashboard"). Missing keys/languages fall back to English. Language is
 * read from localStorage("fx_lang"); the language menu reloads the app so
 * every t() re-evaluates with the new language.
 */
type Lang = "en" | "ar" | "ur" | "hi" | "es" | "vi" | "zh";

export function curLang(): Lang {
  return (localStorage.getItem("fx_lang") as Lang) || "en";
}

// [ar, ur, hi, es, vi, zh]
const D: Record<string, [string, string, string, string, string, string]> = {
  // ── sidebar (top-level) ──
  "Dashboard": ["لوحة التحكم", "ڈیش بورڈ", "डैशबोर्ड", "Panel", "Bảng điều khiển", "仪表板"],
  "Admin Profile": ["ملف المسؤول", "ایڈمن پروفائل", "एडमिन प्रोफ़ाइल", "Perfil de admin", "Hồ sơ quản trị", "管理员资料"],
  "Manage Client": ["إدارة العملاء", "کلائنٹ مینجمنٹ", "क्लाइंट प्रबंधन", "Gestionar clientes", "Quản lý khách hàng", "管理客户"],
  "Partner Management": ["إدارة الشركاء", "پارٹنر مینجمنٹ", "पार्टनर प्रबंधन", "Gestión de socios", "Quản lý đối tác", "合作伙伴管理"],
  "Manage Request": ["إدارة الطلبات", "درخواست مینجمنٹ", "अनुरोध प्रबंधन", "Gestionar solicitudes", "Quản lý yêu cầu", "管理请求"],
  "Reports": ["التقارير", "رپورٹس", "रिपोर्ट", "Informes", "Báo cáo", "报告"],
  "Manage Admin": ["إدارة المسؤولين", "ایڈمن مینجمنٹ", "एडमिन प्रबंधन", "Gestionar admins", "Quản lý quản trị", "管理管理员"],
  "Manager Settings": ["إعدادات المدير", "مینیجر سیٹنگز", "प्रबंधक सेटिंग्स", "Ajustes de gestor", "Cài đặt quản lý", "经理设置"],
  "Finance": ["المالية", "فنانس", "वित्त", "Finanzas", "Tài chính", "财务"],
  "Fund Transfer": ["تحويل الأموال", "فنڈ ٹرانسفر", "फंड ट्रांसफर", "Transferencia de fondos", "Chuyển tiền", "资金转账"],
  "Group Settings": ["إعدادات المجموعات", "گروپ سیٹنگز", "समूह सेटिंग्स", "Ajustes de grupo", "Cài đặt nhóm", "组设置"],
  "KYC Management": ["إدارة KYC", "KYC مینجمنٹ", "KYC प्रबंधन", "Gestión KYC", "Quản lý KYC", "KYC 管理"],
  "Manage Trade": ["إدارة التداول", "ٹریڈ مینجمنٹ", "ट्रेड प्रबंधन", "Gestionar operaciones", "Quản lý giao dịch", "管理交易"],
  "Manage Accounts": ["إدارة الحسابات", "اکاؤنٹ مینجمنٹ", "खाता प्रबंधन", "Gestionar cuentas", "Quản lý tài khoản", "管理账户"],
  "Manage Banks": ["إدارة البنوك", "بینک مینجمنٹ", "बैंक प्रबंधन", "Gestionar bancos", "Quản lý ngân hàng", "管理银行"],
  "Offers": ["العروض", "آفرز", "ऑफ़र", "Ofertas", "Ưu đãi", "优惠"],
  "Support Tickets": ["تذاكر الدعم", "سپورٹ ٹکٹس", "सपोर्ट टिकट", "Tickets de soporte", "Phiếu hỗ trợ", "支持工单"],
  "Settings": ["الإعدادات", "سیٹنگز", "सेटिंग्स", "Ajustes", "Cài đặt", "设置"],
  // ── dashboard ──
  "Admin Dashboard": ["لوحة تحكم المسؤول", "ایڈمن ڈیش بورڈ", "एडमिन डैशबोर्ड", "Panel de administración", "Bảng điều khiển quản trị", "管理仪表板"],
  "Overview": ["نظرة عامة", "جائزہ", "अवलोकन", "Resumen", "Tổng quan", "概览"],
  "Today Deposit": ["إيداع اليوم", "آج کا ڈپازٹ", "आज का डिपॉज़िट", "Depósito de hoy", "Nạp hôm nay", "今日存款"],
  "Total Clients": ["إجمالي العملاء", "کل کلائنٹس", "कुल क्लाइंट", "Total de clientes", "Tổng khách hàng", "客户总数"],
  "Total Partners": ["إجمالي الشركاء", "کل پارٹنرز", "कुल पार्टनर", "Total de socios", "Tổng đối tác", "合作伙伴总数"],
  "Total Deposit": ["إجمالي الإيداع", "کل ڈپازٹ", "कुल डिपॉज़िट", "Depósito total", "Tổng nạp", "总存款"],
  "Revenue Reports": ["تقارير الإيرادات", "ریونیو رپورٹس", "राजस्व रिपोर्ट", "Informes de ingresos", "Báo cáo doanh thu", "收入报告"],
  "KYC Overview": ["نظرة عامة على KYC", "KYC جائزہ", "KYC अवलोकन", "Resumen KYC", "Tổng quan KYC", "KYC 概览"],
  "Support Tracker": ["متتبع الدعم", "سپورٹ ٹریکر", "सपोर्ट ट्रैकर", "Seguimiento de soporte", "Theo dõi hỗ trợ", "支持跟踪"],
  "Pending": ["قيد الانتظار", "زیر التواء", "लंबित", "Pendiente", "Đang chờ", "待处理"],
  // ── topbar ──
  "Search anything…": ["ابحث عن أي شيء…", "کچھ بھی تلاش کریں…", "कुछ भी खोजें…", "Buscar…", "Tìm kiếm bất kỳ…", "搜索任何内容…"],
};

const IDX: Record<Exclude<Lang, "en">, number> = { ar: 0, ur: 1, hi: 2, es: 3, vi: 4, zh: 5 };

export function t(s: string): string {
  const l = curLang();
  if (l === "en") return s;
  const row = D[s];
  return row ? row[IDX[l]] : s;
}
